import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  TextInput,
  FlatList,
  StyleSheet,
  SafeAreaView,
  ActivityIndicator,
  Alert,
  Switch,
  Platform,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { MaterialIcons } from '@expo/vector-icons';
import { documentSharingAPI } from '@/services/api';
import { colors } from '@/design-system/theme/colors';
import { useTranslation } from '@/hooks/useTranslation';
import type { ShareRecipient } from '@/types/sharing';
import { actionErrorMessage } from '@/screens/chat/chatErrors';
import { brand } from '../../design-system/theme/tokens';

interface ShareDocumentModalProps {
  visible: boolean;
  onClose: () => void;
  documentId: number;
  documentName: string;
  isStudent?: boolean;
  hasExistingConsent?: boolean;
  onShareSuccess: () => void;
}

type Step = 'consent' | 'recipients' | 'note';

/**
 * ShareDocumentModal - Simple 3-step share flow
 *
 * Steps:
 * 1. Consent (students only, first share only)
 * 2. Select recipients
 * 3. Add optional note and send
 */
export function ShareDocumentModal({
  visible,
  onClose,
  documentId,
  documentName,
  isStudent = false,
  hasExistingConsent = false,
  onShareSuccess,
}: ShareDocumentModalProps) {
  const { t } = useTranslation();

  // Determine starting step
  const getInitialStep = (): Step => {
    if (isStudent && !hasExistingConsent) return 'consent';
    return 'recipients';
  };

  const [step, setStep] = useState<Step>(getInitialStep());
  const [consentChecked, setConsentChecked] = useState(false);
  const [recipients, setRecipients] = useState<ShareRecipient[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [shareWithAll, setShareWithAll] = useState(false);
  const [shareWithAllMembers, setShareWithAllMembers] = useState(false);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setStep(getInitialStep());
      setConsentChecked(false);
      setSelectedIds(new Set());
      setShareWithAll(false);
      setShareWithAllMembers(false);
      setNote('');
      setError(null);
      fetchRecipients();
    }
  }, [visible]);

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const data = await documentSharingAPI.getRecipients();
      setRecipients(data);
    } catch (err: any) {
      setError(t('share.error'));
    } finally {
      setLoading(false);
    }
  };

  const toggleRecipient = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    // If manually selecting, turn off broadcasts
    if (shareWithAll) setShareWithAll(false);
    if (shareWithAllMembers) setShareWithAllMembers(false);
  };

  const handleShareWithAllToggle = (value: boolean) => {
    setShareWithAll(value);
    if (value) {
      setSelectedIds(new Set());
      setShareWithAllMembers(false);
    }
  };

  const handleShareWithAllMembersToggle = (value: boolean) => {
    setShareWithAllMembers(value);
    if (value) {
      setSelectedIds(new Set());
      setShareWithAll(false);
    }
  };

  const isBroadcast = shareWithAll || shareWithAllMembers;
  const canProceedFromRecipients = isBroadcast || selectedIds.size > 0;

  const handleSubmit = async () => {
    if (!canProceedFromRecipients) {
      setError(t('share.selectAtLeastOne'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await documentSharingAPI.shareDocument(documentId, {
        recipient_user_ids: isBroadcast ? undefined : Array.from(selectedIds),
        share_with_all_students: shareWithAll || undefined,
        share_with_all_members: shareWithAllMembers || undefined,
        note: note.trim() || undefined,
        consent_confirmed: isStudent ? consentChecked : undefined,
      });

      onShareSuccess();
      onClose();
    } catch (err: any) {
      setError(actionErrorMessage(t, err, 'share.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const goToNextStep = () => {
    if (step === 'consent') {
      setStep('recipients');
    } else if (step === 'recipients') {
      if (canProceedFromRecipients) {
        setStep('note');
      } else {
        setError(t('share.selectAtLeastOne'));
      }
    }
  };

  const goToPreviousStep = () => {
    if (step === 'note') {
      setStep('recipients');
    } else if (step === 'recipients' && isStudent && !hasExistingConsent) {
      setStep('consent');
    }
  };

  const renderConsentStep = () => (
    <View style={styles.stepContainer}>
      <View style={styles.consentCard}>
        <MaterialIcons name="verified-user" size={48} color={colors.primary} />
        <Text style={styles.consentTitle}>
          {t('share.consentTitle')}
        </Text>
        <Text style={styles.consentText}>
          {t(
            'share.consentText'
          )}
        </Text>

        <TouchableOpacity
          style={styles.checkboxRow}
          onPress={() => setConsentChecked(!consentChecked)}
          activeOpacity={0.7}
        >
          <View style={[styles.checkbox, consentChecked && styles.checkboxChecked]}>
            {consentChecked && (
              <MaterialIcons name="check" size={18} color="#FFFFFF" />
            )}
          </View>
          <Text style={styles.checkboxLabel}>
            {t('share.iConfirm')}
          </Text>
        </TouchableOpacity>
      </View>

      <TouchableOpacity
        style={[styles.primaryButton, !consentChecked && styles.buttonDisabled]}
        onPress={goToNextStep}
        disabled={!consentChecked}
        activeOpacity={0.8}
      >
        <Text style={styles.primaryButtonText}>
          {t('common.next')}
        </Text>
        <MaterialIcons name="arrow-forward" size={20} color="#FFFFFF" />
      </TouchableOpacity>
    </View>
  );

  const getRoleBadgeStyle = (userType: string) => {
    if (userType === 'b2b2c_student') return { bg: '#ECFDF5', text: '#065F46' };
    if (userType === 'b2b2c_mentor') return { bg: '#EFF6FF', text: brand.primary };
    if (userType === 'b2b2c_admin') return { bg: '#FFFBEB', text: '#92400E' };
    return { bg: colors.gray[100], text: colors.gray[700] };
  };

  const getRoleLabel = (userType: string) => {
    if (userType === 'b2b2c_student') return t('share.roleStudent');
    if (userType === 'b2b2c_mentor') return t('share.roleMentor');
    if (userType === 'b2b2c_admin') return t('share.roleAdmin');
    return userType;
  };

  const renderRecipientItem = ({ item }: { item: ShareRecipient }) => {
    const isSelected = selectedIds.has(item.id);
    const roleStyle = getRoleBadgeStyle(item.user_type);
    return (
      <TouchableOpacity
        style={[styles.recipientItem, isSelected && styles.recipientItemSelected]}
        onPress={() => toggleRecipient(item.id)}
        activeOpacity={0.7}
        disabled={isBroadcast}
      >
        <View style={styles.recipientInfo}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>
              {item.full_name.charAt(0).toUpperCase()}
            </Text>
          </View>
          <View style={styles.recipientDetails}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={styles.recipientName}>{item.full_name}</Text>
              <View style={[styles.roleBadge, { backgroundColor: roleStyle.bg }]}>
                <Text style={[styles.roleBadgeText, { color: roleStyle.text }]}>
                  {getRoleLabel(item.user_type)}
                </Text>
              </View>
            </View>
            <Text style={styles.recipientEmail}>{item.email}</Text>
          </View>
        </View>
        <View style={[styles.radioCircle, isSelected && styles.radioCircleSelected]}>
          {isSelected && <View style={styles.radioInner} />}
        </View>
      </TouchableOpacity>
    );
  };

  const renderRecipientsStep = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>
        {t('share.selectRecipients')}
      </Text>

      {/* Broadcast toggles - only for non-students (mentors/admins) */}
      {!isStudent && (
        <View style={styles.broadcastSection}>
          <View style={styles.allStudentsRow}>
            <View style={styles.allStudentsInfo}>
              <MaterialIcons name="school" size={24} color={colors.primary} />
              <Text style={styles.allStudentsText}>
                {t('share.allStudents')}
              </Text>
            </View>
            <Switch
              value={shareWithAll}
              onValueChange={handleShareWithAllToggle}
              trackColor={{ false: colors.gray[200], true: colors.primary }}
              thumbColor="#FFFFFF"
            />
          </View>
          <View style={styles.allStudentsRow}>
            <View style={styles.allStudentsInfo}>
              <MaterialIcons name="groups" size={24} color={colors.primary} />
              <Text style={styles.allStudentsText}>
                {t('share.allMembers')}
              </Text>
            </View>
            <Switch
              value={shareWithAllMembers}
              onValueChange={handleShareWithAllMembersToggle}
              trackColor={{ false: colors.gray[200], true: colors.primary }}
              thumbColor="#FFFFFF"
            />
          </View>
        </View>
      )}

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={recipients}
          keyExtractor={(item) => String(item.id)}
          renderItem={renderRecipientItem}
          style={[styles.recipientList, isBroadcast && styles.recipientListDisabled]}
          ListEmptyComponent={
            <Text style={styles.emptyText}>
              {t('share.noRecipients')}
            </Text>
          }
        />
      )}

      {error && <Text style={styles.errorText}>{error}</Text>}

      <View style={styles.buttonRow}>
        {(isStudent && !hasExistingConsent) && (
          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={goToPreviousStep}
            activeOpacity={0.7}
          >
            <MaterialIcons name="arrow-back" size={20} color={colors.gray[700]} />
            <Text style={styles.secondaryButtonText}>{t('common.back')}</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={[
            styles.primaryButton,
            styles.flexButton,
            !canProceedFromRecipients && styles.buttonDisabled,
          ]}
          onPress={goToNextStep}
          disabled={!canProceedFromRecipients}
          activeOpacity={0.8}
        >
          <Text style={styles.primaryButtonText}>{t('common.next')}</Text>
          <MaterialIcons name="arrow-forward" size={20} color="#FFFFFF" />
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderNoteStep = () => (
    <KeyboardAvoidingView
      style={styles.stepContainer}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <Text style={styles.stepTitle}>
        {t('share.addNote')}
      </Text>

      <View style={styles.summaryCard}>
        <MaterialIcons name="description" size={20} color={colors.gray[500]} />
        <Text style={styles.summaryText} numberOfLines={1}>
          {documentName}
        </Text>
      </View>

      <View style={styles.recipientsSummary}>
        <MaterialIcons name="people" size={20} color={colors.gray[500]} />
        <Text style={styles.summaryText}>
          {shareWithAll
            ? t('share.allStudents')
            : shareWithAllMembers
            ? t('share.allMembers')
            : t('share.selectedCount', { count: selectedIds.size })}
        </Text>
      </View>

      <TextInput
        style={styles.noteInput}
        placeholder={t('share.notePlaceholder')}
        placeholderTextColor={colors.gray[400]}
        value={note}
        onChangeText={setNote}
        multiline
        numberOfLines={4}
        textAlignVertical="top"
      />

      {error && <Text style={styles.errorText}>{error}</Text>}

      <View style={styles.buttonRow}>
        <TouchableOpacity
          style={styles.secondaryButton}
          onPress={goToPreviousStep}
          activeOpacity={0.7}
        >
          <MaterialIcons name="arrow-back" size={20} color={colors.gray[700]} />
          <Text style={styles.secondaryButtonText}>{t('common.back')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.primaryButton, styles.shareButton, submitting && styles.buttonDisabled]}
          onPress={handleSubmit}
          disabled={submitting}
          activeOpacity={0.8}
        >
          {submitting ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <>
              <MaterialIcons name="send" size={20} color="#FFFFFF" />
              <Text style={styles.primaryButtonText}>
                {t('share.send')}
              </Text>
            </>
          )}
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );

  const getStepNumber = () => {
    const steps: Step[] = isStudent && !hasExistingConsent
      ? ['consent', 'recipients', 'note']
      : ['recipients', 'note'];
    return steps.indexOf(step) + 1;
  };

  const getTotalSteps = () => {
    return isStudent && !hasExistingConsent ? 3 : 2;
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <SafeAreaView style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity
            onPress={onClose}
            style={styles.closeButton}
            accessibilityLabel="Close"
          >
            <MaterialIcons name="close" size={24} color={colors.gray[700]} />
          </TouchableOpacity>
          <View style={styles.headerCenter}>
            <Text style={styles.headerTitle}>
              {t('share.title')}
            </Text>
            <Text style={styles.stepIndicator}>
              {t('share.stepOf', {
                current: getStepNumber(),
                total: getTotalSteps(),
              })}
            </Text>
          </View>
          <View style={styles.headerSpacer} />
        </View>

        {/* Progress bar */}
        <View style={styles.progressBar}>
          <View
            style={[
              styles.progressFill,
              { width: `${(getStepNumber() / getTotalSteps()) * 100}%` },
            ]}
          />
        </View>

        {/* Content */}
        {step === 'consent' && renderConsentStep()}
        {step === 'recipients' && renderRecipientsStep()}
        {step === 'note' && renderNoteStep()}
      </SafeAreaView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  closeButton: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text.primary,
  },
  stepIndicator: {
    fontSize: 12,
    color: colors.text.muted,
    marginTop: 2,
  },
  headerSpacer: {
    width: 44,
  },
  progressBar: {
    height: 4,
    backgroundColor: colors.gray[200],
  },
  progressFill: {
    height: '100%',
    backgroundColor: colors.primary,
  },
  stepContainer: {
    flex: 1,
    padding: 20,
  },
  stepTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: 20,
  },
  // Consent step
  consentCard: {
    backgroundColor: colors.surface,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    marginBottom: 24,
    borderWidth: 1,
    borderColor: colors.border,
  },
  consentTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text.primary,
    marginTop: 16,
    marginBottom: 12,
  },
  consentText: {
    fontSize: 15,
    color: colors.text.secondary,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 20,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: colors.background,
    borderRadius: 12,
    width: '100%',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: colors.gray[300],
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  checkboxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  checkboxLabel: {
    fontSize: 15,
    fontWeight: '500',
    color: colors.text.primary,
    flex: 1,
  },
  // Recipients step
  broadcastSection: {
    gap: 8,
    marginBottom: 16,
  },
  allStudentsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.surface,
    padding: 16,
    borderRadius: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: colors.border,
  },
  allStudentsInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  allStudentsText: {
    fontSize: 16,
    fontWeight: '500',
    color: colors.text.primary,
  },
  roleBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
  },
  roleBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.3,
  },
  recipientList: {
    flex: 1,
    marginBottom: 16,
  },
  recipientListDisabled: {
    opacity: 0.5,
  },
  recipientItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: colors.background,
    borderRadius: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  recipientItemSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.interactive.hover,
  },
  recipientInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  recipientDetails: {
    flex: 1,
  },
  recipientName: {
    fontSize: 15,
    fontWeight: '500',
    color: colors.text.primary,
  },
  recipientEmail: {
    fontSize: 13,
    color: colors.text.muted,
    marginTop: 2,
  },
  radioCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: colors.gray[300],
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioCircleSelected: {
    borderColor: colors.primary,
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: colors.primary,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    textAlign: 'center',
    color: colors.text.muted,
    fontSize: 15,
    marginTop: 40,
  },
  // Note step
  summaryCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: colors.surface,
    padding: 14,
    borderRadius: 10,
    marginBottom: 12,
  },
  recipientsSummary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: colors.surface,
    padding: 14,
    borderRadius: 10,
    marginBottom: 20,
  },
  summaryText: {
    fontSize: 14,
    color: colors.text.secondary,
    flex: 1,
  },
  noteInput: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 12,
    padding: 16,
    fontSize: 16, // >=16 stops iOS focus-zoom (FE-17)
    color: colors.text.primary,
    minHeight: 120,
    marginBottom: 20,
  },
  // Buttons
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 'auto',
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: colors.primary,
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
  },
  flexButton: {
    flex: 1,
  },
  shareButton: {
    flex: 1,
  },
  primaryButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: colors.gray[100],
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 12,
  },
  secondaryButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: colors.gray[700],
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  errorText: {
    color: colors.error,
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 12,
  },
});

export default ShareDocumentModal;
